package fr.frux.feature.harvestdetail

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.core.designsystem.components.UserModerationMessage
import fr.frux.core.designsystem.components.UserReportReason
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestStatus
import fr.frux.shared.domain.model.HarvestTarget
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.domain.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * ViewModel for Harvest Detail screen.
 *
 * Manages:
 * - Harvest details, puis l'arbre concerné (chargement séparé, non bloquant)
 * - Join/leave optimiste avec rollback (garde anti double-tap)
 */
class HarvestDetailViewModel(
    private val harvestId: Long,
    private val harvestRepository: HarvestRepository,
    private val treeRepository: TreeRepository,
    /**
     * Provider lu à chaque usage, pas un snapshot (#264) : ce VM survit à un login déclenché
     * depuis sa propre invite de connexion (Nav3 réutilise le ViewModelStore de l'entrée
     * HarvestDetail à la reprise post-`onAuthenticated()`, la Composable n'est jamais recréée) —
     * un `Long?` figé à la construction resterait `null` après connexion. [loadHarvestDetail]
     * réinjecte la valeur fraîche dans l'état à chaque chargement pour qu'`isParticipant`/
     * `isOrganizer` suivent la session courante.
     */
    private val currentUserIdProvider: () -> Long?,
    /** Gestes de l'ADR 0029 (#515) sur l'organisateur. */
    private val userRepository: UserRepository,
    /** Lieu d'une récolte de lieu (ADR 0035). */
    private val siteRepository: SiteRepository,
) : ViewModel() {
    companion object {
        private const val TAG = "HarvestDetailViewModel"
    }

    private val _state = MutableStateFlow(HarvestDetailState(currentUserId = currentUserIdProvider()))
    val state: StateFlow<HarvestDetailState> = _state.asStateFlow()

    private val moderation =
        HarvestDetailModerationController(
            harvestId = harvestId,
            harvestRepository = harvestRepository,
            userRepository = userRepository,
            scope = viewModelScope,
            state = _state,
            requireLogin = ::requestModeration,
        )

    init {
        loadHarvestDetail()
    }

    /**
     * Retry loading the harvest details (used by [fr.frux.core.designsystem.components.ErrorState]).
     */
    fun retry() = loadHarvestDetail()

    private fun loadHarvestDetail() {
        viewModelScope.launch {
            Logger.d(TAG, "Loading harvest detail: id=$harvestId")
            // Session fraîche à chaque chargement (#264) : un retour post-login réutilise ce VM,
            // isParticipant/isOrganizer doivent refléter l'utilisateur courant, pas celui figé à
            // la construction.
            _state.update {
                it.copy(isLoading = true, error = null, notFound = false, currentUserId = currentUserIdProvider())
            }

            harvestRepository
                .getHarvestById(harvestId)
                .collect { result ->
                    result.fold(
                        onSuccess = { harvest ->
                            Logger.i(TAG, "✅ Loaded harvest $harvestId (${harvest.status})")
                            _state.update {
                                it.copy(harvest = harvest, isLoading = false, error = null)
                            }
                            when (val target = harvest.target) {
                                is HarvestTarget.Tree -> loadTree(target.id)
                                is HarvestTarget.Site -> loadSite(target.id)
                            }
                        },
                        onFailure = { error ->
                            if (error is AppError.NotFound) {
                                // Cas nominal depuis #192 (cancel-on-delete) : la notification
                                // HARVEST_CANCELLED survit à la récolte supprimée avec son arbre.
                                // État dédié (#312), pas l'écran d'erreur générique.
                                Logger.i(TAG, "⚠️ Récolte $harvestId introuvable (annulée ou supprimée)")
                                _state.update { it.copy(isLoading = false, notFound = true) }
                            } else {
                                // Échec attendu (réseau…) : pas de Logger.e, le pont vers Sentry
                                // (#175) en ferait un event (#310).
                                if (error.isExpectedFailure) {
                                    Logger.i(TAG, "⚠️ Chargement de la récolte $harvestId refusé : ${error.message}")
                                } else {
                                    Logger.e(TAG, "❌ Failed to load harvest", error)
                                }
                                _state.update {
                                    it.copy(isLoading = false, error = error.asAppError())
                                }
                            }
                        },
                    )
                }
        }
    }

    /**
     * Charge l'arbre concerné une fois la récolte connue. Un échec ici ne met jamais la fiche en
     * erreur : elle s'affiche quand même, l'encart arbre retombant sur un fallback discret.
     */
    private fun loadTree(treeId: Long) {
        viewModelScope.launch {
            treeRepository
                .getTreeById(treeId)
                .collect { result: Result<Tree> ->
                    result.fold(
                        onSuccess = { tree -> _state.update { it.copy(tree = tree) } },
                        onFailure = { error ->
                            Logger.d(TAG, "⚠️ Arbre indisponible pour la récolte $harvestId : ${error.message}")
                        },
                    )
                }
        }
    }

    /** Même tolérance que [loadTree] : un lieu indisponible n'abîme pas la fiche, le nom de la récolte suffit. */
    private fun loadSite(siteId: Long) {
        viewModelScope.launch {
            siteRepository
                .getSite(siteId)
                .collect { result: Result<Site> ->
                    result.fold(
                        onSuccess = { site -> _state.update { it.copy(site = site) } },
                        onFailure = { error ->
                            Logger.d(TAG, "⚠️ Lieu indisponible pour la récolte $harvestId : ${error.message}")
                        },
                    )
                }
        }
    }

    /** Rejoint la récolte. Optimiste puis rollback si le serveur refuse. */
    fun onJoinClick() = joinOrLeave(join = true)

    /** Quitte la récolte. Optimiste puis rollback si le serveur refuse (ex. organisateur). */
    fun onLeaveClick() = joinOrLeave(join = false)

    private fun joinOrLeave(join: Boolean) {
        val current = _state.value
        val harvest = current.harvest ?: return
        val uid = currentUserIdProvider()
        if (uid == null) {
            _state.update {
                it.copy(loginPrompt = if (join) HarvestLoginPrompt.Join else HarvestLoginPrompt.Leave)
            }
            return
        }
        if (current.joinLeaveInFlight) return
        // Reprise post-login (#668) : ce VM survit sans rechargement, l'état porte encore l'id
        // anonyme du premier chargement. On le réaligne sur la session avant de dériver
        // isParticipant, et un compte déjà dans la liste n'a rien à rejoindre.
        if (current.isStaleSessionFor(uid)) {
            _state.update { it.copy(currentUserId = uid) }
            if (join == (uid in harvest.participants)) return
        }

        val optimistic: Harvest =
            if (join) {
                harvest.copy(
                    participants = harvest.participants + uid,
                    participantsCount = harvest.participantsCount + 1,
                )
            } else {
                harvest.copy(
                    participants = harvest.participants - uid,
                    participantsCount = (harvest.participantsCount - 1).coerceAtLeast(0),
                )
            }
        _state.update { it.copy(harvest = optimistic, joinLeaveInFlight = true, actionError = null) }

        viewModelScope.launch {
            val result =
                if (join) {
                    harvestRepository.joinHarvest(
                        harvestId,
                    )
                } else {
                    harvestRepository.leaveHarvest(harvestId)
                }
            result.fold(
                onSuccess = { updated ->
                    Logger.i(TAG, "✅ ${if (join) "Rejoint" else "Quitté"} la récolte $harvestId")
                    _state.update { it.copy(harvest = updated, joinLeaveInFlight = false) }
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ ${if (join) "Rejoindre" else "Quitter"} la récolte échoué", error)
                    _state.update {
                        it.copy(harvest = harvest, joinLeaveInFlight = false, actionError = error.asAppError())
                    }
                },
            )
        }
    }

    /**
     * Change le statut de la récolte (organisateur uniquement, #226). Pas d'optimisme ici
     * (contrairement à join/leave) : action rare et sérieuse — spinner (`statusChangeInFlight`),
     * état remplacé par le [Harvest] retourné. Échec (409 transition invalide, 403) → snackbar +
     * refresh du détail, l'état affiché pouvant être périmé (ex. un autre organisateur a déjà agi).
     */
    fun onStatusChangeClick(target: HarvestStatus) {
        val current = _state.value
        if (current.statusChangeInFlight) return
        if (current.harvest == null) return

        _state.update { it.copy(statusChangeInFlight = true, actionError = null) }

        viewModelScope.launch {
            val result = harvestRepository.updateHarvestStatus(harvestId, target)
            result.fold(
                onSuccess = { updated ->
                    Logger.i(TAG, "✅ Statut de la récolte $harvestId changé vers $target")
                    _state.update { it.copy(harvest = updated, statusChangeInFlight = false) }
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Changement de statut de la récolte $harvestId vers $target échoué", error)
                    _state.update { it.copy(statusChangeInFlight = false, actionError = error.asAppError()) }
                    loadHarvestDetail()
                },
            )
        }
    }

    /** Consomme l'erreur de join/leave après affichage (snackbar). */
    fun consumeActionError() {
        _state.update { it.copy(actionError = null) }
    }

    /** Consomme l'invite de connexion après affichage (snackbar, #264). */
    fun consumeLoginPrompt() {
        _state.update { it.copy(loginPrompt = null) }
    }

    /** Vrai si un compte est connecté, sinon pose l'invite (#264). */
    fun requestModeration(): Boolean {
        if (currentUserIdProvider() != null) return true
        _state.update { it.copy(loginPrompt = HarvestLoginPrompt.Moderate) }
        return false
    }

    /** Signale la description (ADR 0029) : file de triage, aucun effet sur la récolte. */
    fun onReportDescriptionClick() = moderation.onReportDescriptionClick()

    fun onReportOrganizer(
        reason: UserReportReason,
        details: String?,
    ) = moderation.onReportOrganizer(reason, details)

    fun onBlockOrganizerConfirmed() = moderation.onBlockOrganizerConfirmed()

    fun consumeModerationMessage() = moderation.consumeModerationMessage()
}

/**
 * State for Harvest Detail screen.
 *
 * [isParticipant] et [isOrganizer] sont dérivés de [currentUserId] : `false` pour un visiteur
 * anonyme (id null) comme pour une récolte pas encore chargée. [currentUserId] est réinjecté par
 * [HarvestDetailViewModel.loadHarvestDetail] à chaque chargement (#264) : ce n'est plus un
 * snapshot de construction.
 */
data class HarvestDetailState(
    val harvest: Harvest? = null,
    val tree: Tree? = null,
    val site: Site? = null,
    val isLoading: Boolean = false,
    /** Catégorie d'échec du chargement (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    /** La récolte n'existe plus (404) : annulée ou supprimée avec son arbre (#192, #312). */
    val notFound: Boolean = false,
    val joinLeaveInFlight: Boolean = false,
    val statusChangeInFlight: Boolean = false,
    /** Catégorie d'échec d'une action (#176) ; le libellé est résolu par l'écran. */
    val actionError: AppError? = null,
    /** Invite de connexion (#264) à afficher (snackbar) puis consommer, posée quand un visiteur
     *  anonyme tente rejoindre/quitter. `null` = pas d'invite en attente. */
    val loginPrompt: HarvestLoginPrompt? = null,
    val isModerationPending: Boolean = false,
    val moderationMessage: UserModerationMessage? = null,
    private val currentUserId: Long? = null,
) {
    val isParticipant: Boolean
        get() = harvest != null && currentUserId != null && currentUserId in harvest.participants

    val isOrganizer: Boolean
        get() = harvest != null && currentUserId != null && currentUserId == harvest.createdBy

    /** L'organisateur peut être signalé ou masqué (ADR 0029) : pas sur sa propre récolte. */
    val canModerateOrganizer: Boolean
        get() = harvest?.createdByUsername != null && !isOrganizer

    val canReportDescription: Boolean
        get() = harvest != null && !isOrganizer && !harvest.description.isNullOrBlank()

    internal fun isStaleSessionFor(userId: Long?): Boolean = currentUserId != userId
}

/**
 * Geste qui a exigé un compte (#264, typé en #176) : l'écran choisit le libellé de l'invite, le
 * ViewModel ne connaît que l'intention.
 */
enum class HarvestLoginPrompt {
    Join,
    Leave,
    Moderate,
}
